import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Loader2, X, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-editable page intro. Fetches `/api/page-intros/<pageKey>` and either
 * renders the stored HTML or the hard-coded fallback below for guest / member
 * viewers. Admins see a small pencil button that opens a TipTap editor modal.
 *
 * Props:
 *   pageKey  — one of: home, pillars, events, library, membership, forum
 *   fallback — JSX to render when no custom intro is saved yet
 *   className — wrapper classes (margin, max-width, etc.)
 */
export default function EditablePageIntro({ pageKey, fallback = null, className = '' }) {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [html, setHtml] = useState(null); // null = loading, '' = empty, string = content
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_URL}/api/page-intros/${pageKey}`)
            .then(({ data }) => { if (!cancelled) setHtml(data?.html || ''); })
            .catch(() => { if (!cancelled) setHtml(''); });
        return () => { cancelled = true; };
    }, [pageKey]);

    const openEditor = () => {
        setDraft(html || '');
        setOpen(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await axios.put(
                `${API_URL}/api/page-intros/${pageKey}`,
                { html: draft },
                { withCredentials: true }
            );
            setHtml(data?.html || '');
            setOpen(false);
            toast.success('Page intro updated');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not save');
        } finally {
            setSaving(false);
        }
    };

    // Still loading — avoid flashing the fallback for a split second
    if (html === null) {
        return (
            <div className={className}>
                <div className="h-6" />
            </div>
        );
    }

    const hasCustom = !!(html && html.trim());

    return (
        <div className={`relative group ${className}`} data-testid={`page-intro-${pageKey}`}>
            {hasCustom ? (
                <div
                    className="tiptap-content prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ) : (
                <>{fallback}</>
            )}

            {isAdmin && (
                <button
                    type="button"
                    onClick={openEditor}
                    className="absolute -top-1 right-0 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity inline-flex items-center gap-1 text-xs px-2 py-1 bg-white border border-[#E5E0D8] text-[#5B2D8E] rounded-sm shadow-sm hover:bg-[#F9F6F0]"
                    data-testid={`edit-intro-btn-${pageKey}`}
                    title="Edit this intro (admin)"
                    aria-label={`Edit ${pageKey} intro`}
                >
                    <Pencil className="w-3 h-3" />
                    Edit
                </button>
            )}

            {open && (
                <div
                    className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
                    onClick={() => !saving && setOpen(false)}
                    data-testid={`edit-intro-modal-${pageKey}`}
                >
                    <div
                        className="bg-white rounded-sm max-w-3xl w-full p-6 max-h-[85vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-serif text-xl text-[#1A1A2E]">
                                Edit page intro — <span className="text-[#5B2D8E] capitalize">{pageKey}</span>
                            </h3>
                            <button
                                type="button"
                                onClick={() => !saving && setOpen(false)}
                                className="text-[#4A4A68] hover:text-[#1A1A2E]"
                                aria-label="Close"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            <RichTextEditor
                                value={draft}
                                onChange={setDraft}
                                placeholder="Write the intro that visitors see at the top of this page…"
                                minHeight="220px"
                            />
                            <p className="text-xs text-[#4A4A68] mt-3">
                                Leave empty to restore the default copy.
                            </p>
                        </div>

                        <div className="flex gap-2 justify-end pt-4 border-t border-[#E5E0D8] mt-4">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                disabled={saving}
                                className="btn-secondary text-sm px-4 py-2"
                                data-testid={`cancel-intro-${pageKey}`}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={save}
                                disabled={saving}
                                className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
                                data-testid={`save-intro-${pageKey}`}
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
